// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import classnames from 'classnames';
import React, {useContext, useEffect, useRef, useState, type JSX} from 'react';
import type {WithRouterProps} from 'react-router';
import {withRouter} from 'react-router';

import {Footer, IconButton} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';

import type {FrqGrading} from '../QuestionTypes/FreeResponse/frqGradingStatus';
import {useShowTranslationToggle} from '../QuestionTypes/shared/TranslationSection';
import AnnotationFreezeShield from './Annotations/AnnotationFreezeShield';
import AnnotationNoteIndicators from './Annotations/AnnotationNoteIndicators';
import AnnotationNotesPanel from './Annotations/AnnotationNotesPanel';
import {ANNOTATION_GUTTER_PROPS} from './Annotations/annotationGutter';
import EmptyQuestionShell from './EmptyQuestionShell/EmptyQuestionShell';
import {MobilePracticeViewContext} from './MobilePracticeViewProvider.context';
import {useOptionEliminatorReset} from './OptionEliminatorStore/OptionEliminatorStore';
import './practice-view.scss';
import {PracticeViewWrapper} from './PracticeViewWrapper/PracticeViewWrapper';
import QuestionRenderer from './QuestionRenderer/QuestionRenderer.react';
import QuestionsList from './QuestionsList/QuestionsList.react';
import type {QuestionListProps, QuestionProps} from './QuestionsList/questionsList.types';

interface PracticeViewProps extends WithRouterProps {
  removeQuestionListOnMobile?: boolean; // needed for the time being to conditionally remove questionlist since its not needed on mobile view TODO remove after moving question list into tool bar for AssignmentPracticeView
  activeQuestion: any; // PropTypes.instanceOf(getModelForResourceType('question_v3')), TODO-KBC: Build typescript methods
  activeQuestionSet: any; // PropTypes.instanceOf(getModelForResourceType('question_set_v1')), TODO-KBC: Build typescript methods
  activeQuestionStore: any; // PropTypes.instanceOf(QuestionTypeStore), TODO-KBC: Build typescript methods
  activeGuess: any; // PropTypes.instanceOf(getModelForResourceType('guess_v1')), TODO-KBC: Build typescript methods
  activeSubject: any; // PropTypes.instanceOf(getModelForResourceType('subject_v2')), TODO-KBC: Build typescript methods
  questionProps?: QuestionProps;
  questionRendererProps: {
    actions: React.ReactNode;
    rendererType: 'PreGuess' | 'PostGuess';
    interactive?: boolean;
    navigateBackProps: {
      disabled: boolean;
      handleNavigation?: () => void;
    } | null;
    navigateNextProps: {
      disabled: boolean;
      handleNavigation?: () => void;
    } | null;
    headingActions: React.ReactNode;
    modifyTitle?: () => {};
    /** Puts the prompt under a "Prompt" heading that collapses it. */
    collapsiblePrompt?: boolean;
    /** Set where the response belongs to someone else — a teacher reading a student's submission. */
    isViewingStudentWork?: boolean;
    /** Free response only: the viewed guess's grading state, where a teacher-gated read supplies one. */
    frqGrading?: FrqGrading;
  };
  questionSetProps?: Object;
  questionSets: any; // ImmutablePropTypes.listOf(PropTypes.instanceOf(getModelForResourceType('question_set_v1'))), TODO-KBC: Build typescript methods
  questionsListProps?: QuestionListProps;
  stageComponent?: React.ReactNode;
  showDifficulty?: boolean;
  /**
   * Reserves the margin the note indicators are drawn in. The surface passes its
   * own availability (flag, submission, device), since only it can know — and
   * elsewhere the gutter would narrow the question for an unreachable feature.
   */
  supportsAnnotations?: boolean;
  titleBar?: React.ReactElement<any>;
  isLoading: boolean;
  toolbar?: React.ReactNode;
  activeQuestionDisplayOrientation?: 'default' | 'vertical' | 'horizontal';
  hasQuestionSetAccessPredicate?: (questionSet: any) => boolean;
  QuestionListComponent?: React.ReactElement<any>;
  partialCreditEnabled?: boolean;
  /**
   * When true, the question list and question area share a single bordered card
   * (with an internal vertical divider) instead of rendering as separate panels.
   */
  fusedLayout?: boolean;
  /**
   * When true, the question list is hidden and a floating expand button is
   * rendered at the top-left of the question area.
   */
  isQuestionListCollapsed?: boolean;
  /** Handler for the floating expand button. */
  onExpandQuestionList?: () => void;
  /**
   * Always-mounted content for the right-side panel. The `isRightSidePanelOpen`
   * prop controls its visibility so future enter/exit animations can run.
   */
  rightSidePanel?: React.ReactNode;
  /** Controls visibility of the right-side panel. Defaults to false. */
  isRightSidePanelOpen?: boolean;
  /**
   * Optional content floated over the bottom-right of the question area (e.g.
   * prev/next question navigation in the FRQ grading layout). Inert when unset.
   */
  questionNavOverlay?: React.ReactNode;
}

export const hasQuestionSetAccessPredicateDefaultValue = (questionSet) => {
  const subject = questionSet.getSubject();
  const isRestricted = subject.getMeta().hasRestrictedFreePractice();
  if (sessionStore.isStudent() && isRestricted) {
    return false;
  }
  return (
    (subject.getMeta().isUserHasAccess() as boolean) || (questionSet.getMeta().isFree() as boolean)
  );
};

const PracticeView = ({
  removeQuestionListOnMobile = false,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeGuess,
  activeSubject,
  activeQuestionDisplayOrientation = 'default',
  questionProps = {
    onQuestionClick: () => {}
  },
  // @ts-ignore - yelling b/c object doesn't have correct props initially
  questionRendererProps = {},
  questionSetProps = {},
  questionsListProps,
  questionSets,
  stageComponent,
  showDifficulty = true,
  supportsAnnotations = false,
  titleBar,
  isLoading,
  params,
  toolbar,
  hasQuestionSetAccessPredicate = hasQuestionSetAccessPredicateDefaultValue,
  QuestionListComponent,
  partialCreditEnabled = false,
  fusedLayout = false,
  isQuestionListCollapsed = false,
  onExpandQuestionList,
  rightSidePanel,
  isRightSidePanelOpen = false,
  questionNavOverlay
}: PracticeViewProps) => {
  useOptionEliminatorReset();

  const {studentId} = params;

  const showToggle = useShowTranslationToggle();
  // Container refs for handling scrolling on question updates
  const mainWrapperRef = useRef<HTMLDivElement>(null);
  const answerInputPaneRef = useRef<HTMLDivElement>(null);
  const stackedContainerRef = useRef<HTMLDivElement>(null);
  const postSubmissionScrollPointRef = useRef<HTMLDivElement>(null);
  const listCellRef = useRef<HTMLDivElement>(null);
  const {isMobile, isQuestionsListInView, setIsQuestionsListInView} =
    useContext(MobilePracticeViewContext);

  // The fused list stays mounted while collapsed (so it can animate), which
  // would otherwise leave its buttons keyboard-focusable behind the zero-width
  // cell. `inert` removes them from the tab order and a11y tree; React 16 has
  // no `inert` JSX prop, so toggle it imperatively.
  useEffect(() => {
    listCellRef.current?.toggleAttribute('inert', isQuestionListCollapsed);
  }, [isQuestionListCollapsed]);

  // controlling the position of the question resizer to persist the position when switching between questions
  const [activeQuestionResizerPosition, setActiveQuestionResizerPosition] = useState<
    number | undefined
  >(undefined);

  const makeMobileOnQuestionClick = (args) => {
    return () => {
      if (isMobile && isQuestionsListInView) {
        setIsQuestionsListInView(false);
      }
      questionProps.onQuestionClick(...args)();
    };
  };

  let QuestionListToRender: JSX.Element | null = null;
  if (QuestionListComponent) {
    QuestionListToRender = QuestionListComponent;
  } else if (questionsListProps) {
    QuestionListToRender = (
      <QuestionsList
        {...questionsListProps!}
        className={classnames('practice-view__questions-list flex flex-col', {
          'practice-view__questions-list--in-view': isQuestionsListInView
        })}
        activeQuestion={activeQuestion}
        questionSetProps={{
          ...questionSetProps,
          hasQuestionSetAccessPredicate
        }}
        questionProps={{
          ...questionProps,
          showDifficulty,
          onQuestionClick: (...args) => makeMobileOnQuestionClick(args)
        }}
        questionSets={questionSets}
        isLoading={isLoading}
      />
    );
  }

  return (
    <PracticeViewWrapper
      activeQuestion={activeQuestion}
      questionRendererType={questionRendererProps?.rendererType}
      studentId={studentId}
      mainWrapperRef={mainWrapperRef}
      displayOrientation={activeQuestionDisplayOrientation}
      answerInputPaneRef={answerInputPaneRef}
      stackedContainerRef={stackedContainerRef}
      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
    >
      <div className='practice-view__title-bar'>
        {titleBar &&
          React.cloneElement(
            titleBar,
            isMobile && !isQuestionsListInView && !stageComponent
              ? {
                  imgHref: null,
                  title: 'Questions',
                  backButton: (
                    <TitleBar.BackButton
                      as='button'
                      label='Questions'
                      icon='chevron-left'
                      onClick={() => setIsQuestionsListInView(true)}
                    />
                  )
                }
              : {}
          )}
        {toolbar}
      </div>
      <div className='practice-view__main-wrapper background-subtle flex grow justify-center overflow-hidden'>
        {stageComponent ? (
          <div className='practice-view__stage-component-scroll-handler background-inset absolute w-full overflow-auto'>
            <div className='practice-view__stage-component-wrapper mx-auto'>
              {stageComponent}
              <Footer minimal />
            </div>
          </div>
        ) : (
          <div
            className={classnames('practice-view__main grow overflow-hidden', {
              flex: !fusedLayout,
              // Fused: a grid whose second (panel) track animates 510px <-> 0 —
              // the SAME grid-template-columns mechanism and 250ms-linear timing
              // the list column uses inside the card — so the panel slides
              // exactly like the question list. The card's animated right margin
              // (below) provides the gap so it collapses to nothing when closed.
              'grid grid-rows-[100%] transition-[grid-template-columns] duration-250 ease-linear motion-reduce:transition-none':
                fusedLayout,
              'grid-cols-[minmax(0,1fr)_510px]': fusedLayout && isRightSidePanelOpen,
              'grid-cols-[minmax(0,1fr)_0px]': fusedLayout && !isRightSidePanelOpen,
              // Match the Figma's 16px inset around the fused card. Uses ! to
              // beat the `.practice-view__main { padding: 0 16px }` shorthand
              // from practice-view.scss, which would otherwise zero out the
              // vertical padding.
              'py-x2!': fusedLayout
            })}
          >
            <div
              className={classnames('min-w-0 grow overflow-hidden', {
                // Positioning context for the floating nav overlay (grading
                // layout). This wrapper is viewport-height and non-scrolling, so
                // the overlay pins to the question pane's bottom-right corner
                // instead of scrolling away with the question content.
                relative: !!questionNavOverlay,
                flex: !fusedLayout,
                // Fused: a 2-column grid whose first (list) track animates
                // 360px -> 0 so the list collapses in sync with the right-side
                // panel sliding open. A grid track collapses to an exact 0 —
                // unlike a flex item, which the list's 360px min-width keeps
                // propping open — giving a clean, linear, jump-free slide.
                // grid-rows-[100%]: a single full-height row so both cells get a
                // definite height (the list's virtualized AutoSizer needs it, or
                // it measures 0 and renders no rows).
                'grid grid-rows-[100%] transition-[grid-template-columns,margin] duration-250 ease-linear motion-reduce:transition-none':
                  fusedLayout,
                // 360px must match `$vss_questions-list-width-fused` in
                // view-student-submission.scss (the list's actual rendered
                // width); if that changes, update this track or the column will
                // clip the list / leave dead space.
                'grid-cols-[360px_minmax(0,1fr)]': fusedLayout && !isQuestionListCollapsed,
                'grid-cols-[0px_minmax(0,1fr)]': fusedLayout && isQuestionListCollapsed,
                // Animated gap before the panel: 16px when the panel is open,
                // 0 when closed (collapses with the panel track, same timing).
                'mr-x2': fusedLayout && isRightSidePanelOpen,
                'mr-0': fusedLayout && !isRightSidePanelOpen,
                // Single persistent fused card: border + rounded + bg apply in
                // both collapsed and expanded states. The internal split
                // changes (list+question vs. question only) but the outer card
                // chrome stays put. Suppress the inner QuestionWrapper Card
                // chrome to avoid a nested card-in-card look.
                'rounded-x1 border border-solid border-subtle background-primary [&_.question-wrapper]:rounded-none [&_.question-wrapper]:border-0':
                  fusedLayout
              })}
            >
              {(!isMobile || !removeQuestionListOnMobile) &&
                QuestionListToRender &&
                (fusedLayout ? (
                  // Grid cell for the list. The parent's grid-template-columns
                  // animates the track width; min-w-0 + overflow-hidden let this
                  // cell collapse to 0 while the list keeps its 360px width
                  // inside (clipped, so its virtualized rows don't reflow).
                  // `inert` (toggled via listCellRef) keeps the collapsed list
                  // out of the tab order; aria-hidden/pointer-events-none are
                  // the fallback for browsers without `inert`.
                  <div
                    ref={listCellRef}
                    aria-hidden={isQuestionListCollapsed}
                    className={classnames('min-w-0 flex overflow-hidden', {
                      'pointer-events-none': isQuestionListCollapsed
                    })}
                  >
                    {QuestionListToRender}
                  </div>
                ) : (
                  !isQuestionListCollapsed && QuestionListToRender
                ))}
              {/* Main Question Area */}
              <div
                className={classnames(
                  'practice-view__question-area min-w-0 relative grow overflow-auto',
                  {
                    'practice-view__question-area--translation-enabled': showToggle,
                    // Zero out the legacy 8px top/bottom padding from
                    // practice-view.scss so the question column aligns with
                    // the question list column inside the fused card.
                    'pt-x0! pb-x0!': fusedLayout,
                    // Lock the question's HeadingBar so it aligns with the
                    // list column's header inside the fused card. See note on
                    // ViewStudentSubmissionQuestionList: x10 (80px) instead of
                    // the Figma's 76px because the spacing scale jumps 72→80.
                    '[&_.q-heading-block]:h-x10': fusedLayout
                  }
                )}
                ref={mainWrapperRef}
                data-testid='practice-view__question-area'
                {...(supportsAnnotations ? ANNOTATION_GUTTER_PROPS : {})}
              >
                <AnnotationFreezeShield targetRef={mainWrapperRef} />
                <AnnotationNoteIndicators />
                <div
                  className={classnames('practice-view__question-area h-full grow', {
                    'practice-view__question-area--dim': isMobile && isQuestionsListInView,
                    'mb-x5': isMobile && removeQuestionListOnMobile, // needed since new sticky question list footer on AssignmentPracticeView cuts footer disclaimer off
                    'practice-view__mobile-redesign':
                      activeQuestionDisplayOrientation !== 'default',
                    'pt-x0! pb-x0!': fusedLayout
                  })}
                >
                  {activeQuestion ? (
                    <QuestionRenderer
                      {...questionRendererProps}
                      activeQuestion={activeQuestion}
                      activeQuestionSet={activeQuestionSet}
                      activeQuestionStore={activeQuestionStore}
                      activeGuess={activeGuess}
                      activeSubject={activeSubject}
                      answerInputPaneRef={answerInputPaneRef}
                      displayOrientation={activeQuestionDisplayOrientation}
                      isMobile={isMobile}
                      onResize={(position) => setActiveQuestionResizerPosition(position)}
                      showDifficulty={showDifficulty}
                      resizerPosition={activeQuestionResizerPosition}
                      stackedContainerRef={stackedContainerRef}
                      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
                      hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
                      partialCreditEnabled={partialCreditEnabled}
                      // The fused header is a fixed height, so a wrapped title spills out of it.
                      truncateTitle={fusedLayout}
                      leadingHeaderAction={
                        fusedLayout && isQuestionListCollapsed && onExpandQuestionList ? (
                          <IconButton
                            icon={['far', 'sidebar']}
                            label='Show question list'
                            onClick={onExpandQuestionList}
                            size='s'
                          />
                        ) : null
                      }
                    />
                  ) : (
                    <EmptyQuestionShell />
                  )}
                  {activeQuestionDisplayOrientation === 'default' && !fusedLayout && (
                    <Footer minimal />
                  )}
                </div>
              </div>
              {questionNavOverlay && (
                <div className='right-x2 bottom-x2 absolute z-10'>{questionNavOverlay}</div>
              )}
            </div>
            {fusedLayout &&
              rightSidePanel && (
                // Grid cell for the panel; its column track (on practice-view__main)
                // animates 510px <-> 0, exactly like the list's column track. Kept
                // mounted so it animates both in and out. min-w-0 + overflow-hidden
                // let it collapse to 0 with the track; the border is dropped while
                // closed so a 0-width box shows no seam.
                <aside
                  aria-hidden={!isRightSidePanelOpen}
                  className={classnames(
                    'min-w-0 flex overflow-hidden rounded-x1 background-primary',
                    isRightSidePanelOpen
                      ? 'border border-solid border-subtle'
                      : 'pointer-events-none border-0'
                  )}
                  data-testid='practice-view__right-side-panel'
                >
                  {rightSidePanel}
                </aside>
              )}
          </div>
        )}
        {/* Sibling of `practice-view__main`, so it escapes that box's 16px page
            padding and sits flush against the content region's right edge. The
            fused layout owns its own right slot, so it keeps it. */}
        {!fusedLayout && !stageComponent && (
          <AnnotationNotesPanel questionId={activeQuestion?.getId()} />
        )}
      </div>
    </PracticeViewWrapper>
  );
};

export default withRouter(PracticeView);
